// The words and tones every redesigned screen uses. A status, priority, band
// or kind gets the same label and the same tone wherever it appears; the
// components (StatusTag, PriorityTag, BandTag, KindTag, TimeTag, StateBlock)
// read them from here, and SQL parity checks compare their own stage helpers
// against these tables. Pure data and functions with no React and no CSS
// (scripts/ui-foundation/verify-ui-vocabulary-format.mjs).
//
// Tones follow one rule everywhere:
//   neutral   open, waiting, closed, cancelled, archived, dismissed, inactive
//   outline   draft            dashed  in setup (not created yet)
//   indigo    in review, in progress, assigned, picked up, new, edited
//   amber     awaiting approval or information, second opinion, returned,
//             pending, paused, needs review, due soon
//   green     completed, approved, confirmed, processed, met, live, active
//   red       overdue, past target, breached, failed, rejected, escalated
//   blue      Transaction, and nothing else
//   orange    High priority and the High band; light: quiet supporting tags
export const TONES = Object.freeze(['neutral', 'outline', 'dashed', 'indigo', 'blue', 'green', 'amber', 'orange', 'red', 'light']);

const ELLIPSIS = '…';
const presentation = (label, tone, extra = {}) => Object.freeze({ label, tone, ...extra });
const table = entries => Object.freeze(Object.fromEntries(Object.entries(entries).map(([raw, [label, tone]]) => [raw, presentation(label, tone)])));

export function humanizeLabel(value) {
  const words = String(value ?? '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  return words ? words[0].toUpperCase() + words.slice(1) : '';
}

// Raw stored values per domain. A value missing here is shown in words with
// a neutral tone, so a new server value never breaks a screen.
export const STATUS_DOMAINS = Object.freeze({
  // Investigation cases.
  case:table({
    draft:['Draft', 'outline'],
    open:['Open', 'neutral'],
    in_review:['In review', 'indigo'],
    awaiting_information:['Awaiting information', 'amber'],
    awaiting_approval:['Awaiting approval', 'amber'],
    completed:['Completed', 'green'],
    closed:['Closed', 'neutral'],
    cancelled:['Cancelled', 'neutral'],
    archived:['Archived', 'neutral'],
  }),
  // Automated risk cases (the case queue).
  riskCase:table({
    open:['Open', 'neutral'],
    assigned:['Assigned', 'indigo'],
    in_review:['In review', 'indigo'],
    escalated:['Escalated', 'red'],
    closed:['Closed', 'neutral'],
    dismissed:['Dismissed', 'neutral'],
  }),
  alert:table({
    open:['Open', 'neutral'],
    acknowledged:['Acknowledged', 'indigo'],
    escalated:['Escalated', 'red'],
    resolved:['Resolved', 'green'],
    suppressed:['Suppressed', 'neutral'],
  }),
  // Manual reviews. qa_pending is the four-eyes approval before a review
  // closes; it is not the sampled QA check.
  review:table({
    draft:['Draft', 'outline'],
    open:['Open', 'neutral'],
    under_review:['In review', 'indigo'],
    in_review:['In review', 'indigo'],
    qa_pending:['Awaiting approval', 'amber'],
    returned:['Returned for changes', 'amber'],
    escalated:['Escalated', 'red'],
    closed:['Completed', 'green'],
    completed:['Completed', 'green'],
    cancelled:['Cancelled', 'neutral'],
  }),
  // Quality assurance assignments.
  qa:table({
    queued:['Waiting', 'neutral'],
    assigned:['Waiting', 'neutral'],
    claimed:['Picked up', 'indigo'],
    in_progress:['Being reviewed', 'indigo'],
    awaiting_adjudication:['Second opinion', 'amber'],
    completed:['Finished', 'green'],
    adjudicated:['Finished', 'green'],
    cancelled:['Cancelled', 'neutral'],
  }),
  // Escalation statuses come from configuration: each workflow names its own
  // statuses and colours. These are the status categories; a configured
  // status object keeps its own label and colour (see statusPresentation).
  escalation:table({
    open:['Open', 'neutral'],
    in_progress:['In progress', 'neutral'],
    pending:['Pending', 'neutral'],
    resolved:['Resolved', 'neutral'],
    closed:['Closed', 'neutral'],
  }),
  leave:table({
    pending:['Pending', 'amber'],
    approved:['Approved', 'green'],
    rejected:['Rejected', 'red'],
    cancelled:['Cancelled', 'neutral'],
  }),
  overtime:table({
    pending:['Pending', 'amber'],
    approved:['Approved', 'green'],
    rejected:['Rejected', 'red'],
    cancelled:['Cancelled', 'neutral'],
  }),
  // A leave or overtime decision as it is recorded.
  rotaDecision:table({
    approved:['Approved', 'green'],
    rejected:['Rejected', 'red'],
    cancelled:['Cancelled', 'neutral'],
  }),
  // Risk modules. 'active' is shown as Live.
  module:table({
    active:['Live', 'green'],
    live:['Live', 'green'],
    paused:['Paused', 'amber'],
    draft:['Draft', 'outline'],
    in_setup:['In setup', 'dashed'],
    archived:['Archived', 'neutral'],
  }),
  linkRule:table({
    active:['Active', 'green'],
    draft:['Draft', 'outline'],
    paused:['Paused', 'amber'],
    disabled:['Off', 'neutral'],
    deprecated:['Retired', 'neutral'],
    retired:['Retired', 'neutral'],
  }),
  // The lifecycle of one link between two players.
  linkLifecycle:table({
    detected:['Detected', 'neutral'],
    pending_review:['Sent to review', 'amber'],
    likely_linked:['Likely linked', 'indigo'],
    confirmed:['Confirmed', 'green'],
    rejected:['Rejected', 'red'],
    shared_environment:['Shared environment', 'neutral'],
    suppressed:['Suppressed', 'neutral'],
    monitoring:['Monitoring', 'neutral'],
    expired:['Expired', 'neutral'],
  }),
  // Report and notification deliveries.
  delivery:table({
    pending:['Waiting', 'neutral'],
    queued:['Waiting', 'neutral'],
    retry_scheduled:['Retry scheduled', 'amber'],
    sending:['Sending', 'indigo'],
    processing:['Sending', 'indigo'],
    uncertain:['Needs confirmation', 'amber'],
    sent:['Delivered', 'green'],
    delivered:['Delivered', 'green'],
    completed:['Delivered', 'green'],
    failed:['Failed', 'red'],
    dead_letter:['Failed', 'red'],
    rejected:['Failed', 'red'],
    cancelled:['Cancelled', 'neutral'],
    skipped:['Skipped', 'neutral'],
  }),
  // QA sampling runs.
  samplingRun:table({
    queued:['Waiting', 'neutral'],
    running:['Running', 'indigo'],
    completed:['Completed', 'green'],
    partial:['Partly picked', 'amber'],
    no_matches:['Nothing to pick', 'neutral'],
    failed:['Failed', 'red'],
    cancelled:['Cancelled', 'neutral'],
  }),
  // The status class of an event (from its type's status values). Every
  // accepted event counts, whatever its class. The labels are the event
  // activity labels (eventActivityMeasures.js EVENT_ACTIVITY_STATUS_CLASS_LABELS;
  // parity in scripts/ui-foundation/verify-ui-vocabulary-format.mjs).
  eventStatusClass:table({
    success:['Success', 'green'],
    pending:['Pending', 'amber'],
    failed:['Failed', 'red'],
    unclassified:['Not classified', 'neutral'],
  }),
  // How far ingestion got with an event, grouped exactly as the event lists
  // and their status filter group it (eventActivityView.js
  // EVENT_ACTIVITY_PROCESSING_STATUSES and _LABELS): failed or ignored is
  // Failed, received, processing or held for replay is Processing.
  processingStatus:table({
    received:['Processing', 'indigo'],
    processing:['Processing', 'indigo'],
    replay_held:['Processing', 'indigo'],
    processed:['Processed', 'green'],
    duplicate:['Processed', 'green'],
    ignored:['Failed', 'red'],
    failed:['Failed', 'red'],
  }),
});

export const STATUS_DOMAIN_NAMES = Object.freeze(Object.keys(STATUS_DOMAINS));

const HEX_COLOUR = /^#[0-9a-f]{6}$/i;
const rawKey = value => String(value ?? '').trim();

// statusPresentation('qa', 'claimed') -> { label:'Picked up', tone:'indigo',
// raw:'claimed', known:true }. Escalations also accept a configured status
// ({ status_key, label, category, colour }): it keeps its own label, is
// always neutral, and passes its colour on as `swatch` for ConfiguredStatusTag.
export function statusPresentation(domain, raw) {
  const values = Object.prototype.hasOwnProperty.call(STATUS_DOMAINS, domain) ? STATUS_DOMAINS[domain] : null;
  if (raw && typeof raw === 'object') {
    const key = rawKey(raw.status_key ?? raw.key ?? raw.id ?? raw.category);
    const known = values && Object.prototype.hasOwnProperty.call(values, key) ? values[key] : null;
    const category = values && Object.prototype.hasOwnProperty.call(values, rawKey(raw.category)) ? values[rawKey(raw.category)] : null;
    const label = String(raw.label || '').trim() || known?.label || category?.label || humanizeLabel(key) || 'Not set';
    const swatch = HEX_COLOUR.test(String(raw.colour ?? raw.color ?? '')) ? String(raw.colour ?? raw.color) : null;
    return presentation(label, domain === 'escalation' ? 'neutral' : known?.tone || category?.tone || 'neutral', {
      raw:key, known:Boolean(known || category), configured:true, swatch,
    });
  }
  const key = rawKey(raw);
  if (!key) return presentation('Not set', 'neutral', { raw:'', known:false });
  const known = values && Object.prototype.hasOwnProperty.call(values, key) ? values[key] : null;
  return known
    ? presentation(known.label, known.tone, { raw:key, known:true })
    : presentation(humanizeLabel(key), 'neutral', { raw:key, known:false });
}

export const PRIORITY = Object.freeze({
  urgent:presentation('Urgent', 'red'),
  high:presentation('High', 'orange'),
  medium:presentation('Medium', 'neutral'),
  low:presentation('Low', 'light'),
});

// Risk bands are fills with dark label text (--band-*, --band-on).
export const BAND = Object.freeze({
  low:presentation('Low', 'green', { token:'--band-low' }),
  medium:presentation('Medium', 'amber', { token:'--band-medium' }),
  high:presentation('High', 'orange', { token:'--band-high' }),
  critical:presentation('Critical', 'red', { token:'--band-critical' }),
});

// Blue belongs to Transaction alone.
export const KIND = Object.freeze({
  transaction:presentation('Transaction', 'blue'),
  activity:presentation('Activity', 'neutral'),
  case:presentation('Case', 'indigo'),
  alert:presentation('Alert', 'red'),
  contact:presentation('Customer contact', 'outline'),
});

// Direction is written as text, never as an arrow glyph. The colour belongs
// to the signed amount (--amount-*), so the words themselves stay neutral.
export const DIRECTION = Object.freeze({
  in:presentation('In', 'neutral', { token:'--amount-in' }),
  out:presentation('Out', 'neutral', { token:'--amount-out' }),
  bonus:presentation('Bonus', 'neutral', { token:'--amount-bonus' }),
});

// Every panel that shows values says which time it shows.
export const TIME_CONTEXT = Object.freeze({
  now:presentation('Now', 'neutral', { token:'--now-bg' }),
  period:presentation('In period', 'indigo', { token:'--period-bg' }),
  frozen:presentation('At completion', 'outline', { token:'--frozen-border' }),
});

function lookupPresentation(source, raw) {
  const key = rawKey(raw).toLowerCase();
  if (!key) return presentation('Not set', 'neutral', { raw:'', known:false });
  return Object.prototype.hasOwnProperty.call(source, key)
    ? presentation(source[key].label, source[key].tone, { ...source[key], raw:key, known:true })
    : presentation(humanizeLabel(key), 'neutral', { raw:key, known:false });
}

export const priorityPresentation = raw => lookupPresentation(PRIORITY, raw);
export const bandPresentation = raw => lookupPresentation(BAND, raw);
export const kindPresentation = raw => lookupPresentation(KIND, raw);

// 'In period · 4 Sep – 3 Oct' when a range is given (format it with
// uiFormat.formatRange); frozen values may name their moment ('At last review').
export function timeContextLabel(mode, detail = '') {
  const context = Object.prototype.hasOwnProperty.call(TIME_CONTEXT, mode) ? TIME_CONTEXT[mode] : TIME_CONTEXT.now;
  const text = String(detail || '').trim();
  if (mode === 'frozen') return text || context.label;
  return text ? `${context.label} · ${text}` : context.label;
}

// The standard operations, keyed by the FIELD_OPERATION_DEFINITIONS ids in
// src/fields.js and labelled exactly as there. Fields keeps its full list.
export const OPERATION_LABELS = Object.freeze({
  count:'Count',
  count_distinct:'Count distinct',
  sum:'Sum',
  avg:'Average',
  min:'Minimum',
  max:'Maximum',
  median:'Median',
  percentile:'Percentile',
  standard_deviation:'Standard deviation',
});

export const operationLabel = id => (Object.prototype.hasOwnProperty.call(OPERATION_LABELS, id) ? OPERATION_LABELS[id] : humanizeLabel(id));

// DW-SHARED (the shared kit board, piece 5, note 24): the one set of
// qualifiers a number can carry, with its tag tone and what it means in a
// sentence. "Value unavailable", "estimate" and "Counting since" are not
// qualifiers; an empty value is always the one empty mark (EMPTY_VALUE in
// fieldValueText.js), never a word.
export const QUALIFIERS = Object.freeze({
  at_least:presentation('At least', 'outline', { meaning:'a lower bound, because some of what it counts was not kept' }),
  about:presentation('About', 'outline', { meaning:'an estimate' }),
  partly_counted:presentation('Partly counted', 'amber', { meaning:'not every hour of the period is counted yet' }),
  in_progress:presentation('In progress', 'indigo', { meaning:'the period is not over, so it can still change' }),
  stale:presentation('Stale', 'amber', { meaning:'not updated since then' }),
  not_measured:presentation('Not measured', 'light', { meaning:'nothing was measured for it' }),
});
export const QUALIFIER_IDS = Object.freeze(Object.keys(QUALIFIERS));

// qualifierText('stale', '22 Oct') 'Stale, as of 22 Oct'; an unknown id is ''.
export function qualifierText(id, asOf = '') {
  if (!Object.prototype.hasOwnProperty.call(QUALIFIERS, id)) return '';
  const when = String(asOf || '').trim();
  return id === 'stale' && when ? `${QUALIFIERS.stale.label}, as of ${when}` : QUALIFIERS[id].label;
}

// DW-SHARED (piece 12, note 51): the two export labels, each once, in the
// header's Export menu, and the one permission reason. The menu's line about
// masking and the audit log is ExportMenu's (src/ExportMenu.js): this
// vocabulary carries no masking words.
export const EXPORT_LABELS = Object.freeze({
  menu:'Export',
  shown:'Download shown rows',
  all:'Export all rows',
  noPermission:'Exporting needs the Export reports permission. Ask an admin to add it to your role.',
});

// DW-SHARED (piece 2): the one Add filter control.
export const ADD_FILTER_LABEL = 'Add filter';

// DW-SHARED (piece 7, note 33): the one split sentence.
//   splitSentence(['PSP', 'Country'], 6)  'Split by PSP, then by Country, top 6, rest in Other'
//   splitSentence(['PSP'])                'Split by PSP'
export function splitSentence(keys = [], topN = null) {
  const names = (Array.isArray(keys) ? keys : [keys]).map(key => String(key ?? '').trim()).filter(Boolean);
  if (!names.length) return '';
  const count = Number(topN);
  const top = Number.isInteger(count) && count > 0 ? `, top ${count}, rest in Other` : '';
  return `Split by ${names.join(', then by ')}${top}`;
}

export const CREATE_VERBS = Object.freeze({
  create:'Create',
  raiseEscalation:'Raise escalation',
  startManualReview:'Start manual review',
  requestLeave:'Request leave',
  logOvertime:'Log overtime',
  run:'Run',
  add:'Add',
});

// Words for the states every async region can be in (StateBlock).
export const STATE_ACTIONS = Object.freeze({
  retry:'Try again',
  clearFilters:'Clear filters',
  loadMore:'Load more',
  showMore:'Show more',
  loadOlder:'Load older',
  updating:`Updating${ELLIPSIS}`,
});

const nounText = noun => String(noun ?? '').trim();
const sentence = text => {
  const value = String(text ?? '').trim();
  if (!value) return '';
  const capitalised = value[0].toUpperCase() + value.slice(1);
  return /[.!?]$/.test(capitalised) ? capitalised : `${capitalised}.`;
};

// loadingText('cases') 'Loading cases…'
export const loadingText = noun => (nounText(noun) ? `Loading ${nounText(noun)}${ELLIPSIS}` : `Loading${ELLIPSIS}`);
// emptyText('cases') 'No cases yet.'
export const emptyText = noun => (nounText(noun) ? `No ${nounText(noun)} yet.` : 'Nothing here yet.');
// noMatchText('cases') 'No cases match these filters.'
export const noMatchText = noun => (nounText(noun) ? `No ${nounText(noun)} match these filters.` : 'Nothing matches these filters.');
// errorText('cases', 'The list loaded at 17:40 is still shown')
//   'Cases could not be loaded. The list loaded at 17:40 is still shown.'
export function errorText(noun, stillShown = '') {
  const things = nounText(noun);
  const lead = things ? `${things[0].toUpperCase()}${things.slice(1)} could not be loaded.` : 'This could not be loaded.';
  const kept = sentence(stillShown);
  return kept ? `${lead} ${kept}` : lead;
}
